import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Heart, User, Search, X, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import { useCategories } from "@/hooks/useCategories";
import { useSearchProducts } from "@/hooks/useProducts";
import NotificationBell from '@/components/common/NotificationBell'; 
import { useDebounce } from "@/hooks/useDebounce";
import { formatPrice, ProductType, formatAuthorsDisplay } from "@bookstore/shared";
import Logo from '@/assets/icon.png';

const Header = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { totalItems: cartItemCount } = useCart();
  const { itemCount: wishlistItemCount } = useWishlist();
  const { data: categories, isLoading: categoriesLoading } = useCategories();

  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Refs for click detection
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(searchQuery, 400);
  const { data: searchResults } = useSearchProducts(
    debouncedQuery,
    debouncedQuery.length >= 2
  );

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchRef.current && !searchRef.current.contains(target)) {
        setShowResults(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(target)) {
        setMobileSearchOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // setDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setShowResults(false);
      setMobileSearchOpen(false);
      setSearchQuery("");
    }
  };

  const SearchResults = () => (
    <>
      {showResults && searchQuery.length >= 2 && searchResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No books found
            </div>
          ) : (
            <div className="py-2">
              {searchResults.slice(0, 5).map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.seoUrl}`}
                  onClick={() => {
                    setShowResults(false);
                    setMobileSearchOpen(false);
                    setSearchQuery("");
                  }}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50"
                >
                  <div className="w-12 h-16 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                    {product.images[0] && (
                      <img
                        src={product.images[0]}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {product.title}
                    </p>
                    <p className="text-sm text-gray-600 truncate" title={product.productType === ProductType.BOOK ? product.author : product.brand}>
                      {product.productType === ProductType.BOOK
                        ? formatAuthorsDisplay(product.author, 2)
                        : product.brand}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-blue-600">
                      {formatPrice(
                        product.discountedPrice || product.price
                      )}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <nav className="bg-background border-b border-primary-200 sticky top-0 z-50">
      {/* Main header row */}
      <div className="container-custom mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Hide title on mobile */}
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0" aria-label='homepage'>
            <img

              src={Logo}
              alt="Logo"
              className="h-8 w-8  object-cover bg-primary rounded-full p-1"
            />
            <span className="hidden sm:block text-xl xl:text-2xl font-semibold text-primary whitespace-nowrap">
              BookMyBook
            </span>
          </Link>

          {/* Desktop Navigation Links (xl and above) */}
          <div className="hidden xl:flex items-center space-x-8">
            <Link to="/" className="text-primary font-medium hover:text-primary-800" aria-label='homepage'>
              Home
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="flex items-center text-primary-900 font-medium hover:text-primary-700"
              >
                Categories
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
              {dropdownOpen && (
                <div className="absolute left-0 bg-background border rounded-lg shadow-lg w-44 mt-2 z-50">
                  {!categoriesLoading && categories ? (
                    <>
                      {categories.slice(0, 7).map((category) => (
                        <Link
                          key={category.id}
                          to={`/products/${encodeURIComponent(
                            category.seoUrl
                          )}`}
                          className="block px-4 py-2 hover:bg-primary-200 text-primary-900 hover:text-primary-700"
                          onClick={() => setDropdownOpen(false)}
                          aria-label={`category-${category.seoUrl}`}
                        >
                          {category.name}
                        </Link>
                      ))}
                      {categories.length > 7 && (
                        <Link
                          to="/categories"
                          className="block px-4 py-2 hover:bg-primary-200 text-primary-700 font-semibold border-t border-primary-100"
                          onClick={() => setDropdownOpen(false)}
                          aria-label="all-categories"
                        >
                          See All Categories →
                        </Link>
                      )}
                    </>
                  ) : (
                    <div className="p-4 text-primary-500">Loading...</div>
                  )}
                </div>
              )}
            </div>
            <Link to="/about-us" className="text-primary-900 font-medium hover:text-primary-700" aria-label='about-us'>
              About
            </Link>
            <Link to="/contact-us" className="text-primary-900 font-medium hover:text-primary-700" aria-label='contact-us'>
              Contact
            </Link>
          </div>

          {/* Right side icons */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Desktop Search (xl and above) */}
            <div className="hidden xl:block relative" ref={searchRef}>
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Search for books..."
                  className="pl-10 pr-10 py-2 border border-primary-300 rounded-lg w-64 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setShowResults(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </form>
              <SearchResults />
            </div>

            {/* Mobile Search Button (below xl) */}
            <button
              onClick={() => setMobileSearchOpen((prev) => !prev)}
              className="xl:hidden p-2 hover:bg-primary-100 rounded-lg"
            >
              <Search className="w-5 h-5 sm:w-6 sm:h-6 text-primary-700" />
            </button>

            {/* Wishlist */}
            <Link to="/wishlist" className="relative p-2 hover:bg-primary-200 rounded-lg" aria-label='wishlist'>
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-primary-700" />
              {wishlistItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {wishlistItemCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link to="/cart" className="relative p-2 hover:bg-primary-200 rounded-lg" aria-label='cart'>
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-primary-700" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* Notification Bell */}
            <NotificationBell />
   

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className="flex items-center gap-1 sm:gap-2 p-2 hover:bg-primary-100 rounded-lg"
              >
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary-700" />
                {user && <ChevronDown className="w-4 h-4 text-primary-500" />}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 w-48 bg-background rounded-lg shadow-lg z-50 border border-primary-200">
                  {user ? (
                    <>
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 hover:bg-primary-100 text-primary-700"
                        onClick={() => setUserMenuOpen(false)}
                        aria-label='profile'
                      >
                        Profile
                      </Link>
                      <Link 
                        to="/orders" 
                        className="block px-4 py-2 hover:bg-primary-100 text-primary-700"
                        onClick={() => setUserMenuOpen(false)}
                        aria-label='my-orders'
                      >
                        My Orders
                      </Link>
                      <button
                        onClick={() => {
                          signOut();
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-primary-100 text-primary-700"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="block px-4 py-2 hover:bg-primary-100 text-center text-primary-600 font-medium"
                      onClick={() => setUserMenuOpen(false)}
                      aria-label='login'
                    >
                      Login
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle (below xl) */}
            <button
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="xl:hidden p-2 hover:bg-primary-100 rounded-lg"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-primary-700" />
            </button>
          </div>
        </div>

        {/* Mobile Search Bar (below xl, when opened) */}
        {mobileSearchOpen && (
          <div className="xl:hidden pb-4" ref={mobileSearchRef}>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
                placeholder="Search for books..."
                className="pl-10 pr-10 py-2 border border-primary-300 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:outline-none"
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setShowResults(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-400 hover:text-primary-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>
            <SearchResults />
          </div>
        )}

        {/* Second row: Nav Links (medium screens, center-aligned) */}
        <div className="hidden lg:xl:hidden lg:flex items-center justify-center border-t border-primary-100 py-3">
          <div className="flex items-center space-x-8">
            <Link to="/" className="text-primary-700 font-medium hover:text-primary-800"  aria-label='homepage'>
              Home
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="flex items-center text-primary-900 font-medium hover:text-primary-700"
              >
                Categories
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
              {dropdownOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 bg-white border rounded-lg shadow-lg w-44 mt-2 z-50">
                  {!categoriesLoading && categories ? (
                    <>
                      {categories.slice(0, 7).map((category) => (
                        <Link
                          key={category.id}
                          to={`/products?category=${encodeURIComponent(category.seoUrl)}`}
                          className="block px-4 py-2 hover:bg-primary-50"
                          // onClick={() => setDropdownOpen(false)}
                          aria-label={`category-${category.seoUrl}`}
                        >
                          {category.name}
                        </Link>
                      ))}
                      {categories.length > 7 && (
                        <Link
                          to="/categories"
                          className="block px-4 py-2 hover:bg-primary-50 text-primary-700 font-semibold border-t border-primary-100"
                          aria-label="all-categories"
                        >
                          See All Categories →
                        </Link>
                      )}
                    </>
                  ) : (
                    <div className="p-4 text-primary-500">Loading...</div>
                  )}
                </div>
              )}
            </div>
            <Link to="/about" className="text-primary-900 font-medium hover:text-primary-700" aria-label='about'>
              About
            </Link>
            <Link to="/contact" className="text-primary-900 font-medium hover:text-primary-700" aria-label='contact'>
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu (below xl) */}
      {mobileMenuOpen && (
        <div className="xl:hidden border-t border-primary-200 bg-primary-50">
          <div className="container-custom mx-auto px-4 py-4">
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="block py-2 px-4 text-primary-700 font-medium hover:bg-white rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label='homepage'
                >
                  Home
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="flex items-center justify-between w-full py-2 px-4 text-primary-900 font-medium hover:bg-white rounded-lg"
                >
                  Categories
                  <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="ml-4 mt-2 space-y-1">
                    {!categoriesLoading && categories ? (
                      <>
                        {categories.slice(0, 7).map((category) => (
                          <Link
                            key={category.id}
                            to={`/products?category=${encodeURIComponent(
                              category.name
                            )}`}
                            className="block py-2 px-4 text-primary-700 hover:bg-white rounded-lg"
                            onClick={() => {
                              setDropdownOpen(false);
                              setMobileMenuOpen(false);
                            }}
                            aria-label={`category-${category.seoUrl}`}
                          >
                            {category.name}
                          </Link>
                        ))}
                        {categories.length > 7 && (
                          <Link
                            to="/categories"
                            className="block py-2 px-4 text-primary-700 font-semibold hover:bg-white rounded-lg border-t border-primary-100 mt-2 pt-2"
                            onClick={() => {
                              setDropdownOpen(false);
                              setMobileMenuOpen(false);
                            }}
                            aria-label="all-categories"
                          >
                            See All Categories →
                          </Link>
                        )}
                      </>
                    ) : (
                      <div className="py-2 px-4 text-primary-500">Loading...</div>
                    )}
                  </div>
                )}
              </li>
              <li>
                <Link
                  to="/about-us"
                  className="block py-2 px-4 text-primary-900 font-medium hover:bg-white rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label='about'
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/contact-us"
                  className="block py-2 px-4 text-primary-900 font-medium hover:bg-white rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label='contact'
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;